import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Twitter, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT } from "@/constants/site";
import { Reveal } from "@/lib/motion";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCIAL_ICONS = { Instagram, Facebook, Linkedin, Twitter };

const initialForm = { name: "", email: "", phone: "", message: "" };

export const Contact = ({ content = {} }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const details = {
    ...CONTACT,
    phone: content.phone || CONTACT.phone,
    phoneHref: content.phone_href || CONTACT.phoneHref,
    email: content.email || CONTACT.email,
    address: content.address || CONTACT.address,
  };

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("contactForm.required"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      toast.success(t("contactForm.success"));
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : t("contactForm.error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="iletisim" data-testid="contact-section" className="bg-cream py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Details */}
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              {content.eyebrow || "İletişim"}
            </span>
            <h2 className="mt-4 font-heading font-bold text-ink text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              {content.title || "Bize ulaşın."}
            </h2>
            <p className="mt-5 text-base text-ink/70 leading-relaxed">
              {content.description || "Sorularınız, iş birlikleri ve bayilik talepleriniz için ekibimiz sizinle."}
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={details.phoneHref}
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-detail text-brand">
                  <Phone className="h-5 w-5" />
                </span>
                <span className="text-ink font-medium group-hover:text-brand transition-colors">
                  {details.phone}
                </span>
              </a>
              <a
                href={`mailto:${details.email}`}
                data-testid="contact-email"
                className="flex items-center gap-4 group"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-detail text-brand">
                  <Mail className="h-5 w-5" />
                </span>
                <span className="text-ink font-medium group-hover:text-brand transition-colors">
                  {details.email}
                </span>
              </a>
              <div className="flex items-center gap-4" data-testid="contact-address">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-detail text-brand">
                  <MapPin className="h-5 w-5" />
                </span>
                <span className="text-ink font-medium">{details.address}</span>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              {(content.socials?.length ? content.socials : CONTACT.socials).map((s) => {
                const Icon = SOCIAL_ICONS[s.icon];
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={s.label}
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-detail bg-surface text-ink hover:bg-brand hover:text-cream hover:border-brand transition-colors duration-200"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>

            <iframe
              title={t("contactForm.mapTitle")}
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.2959595167526!2d28.804846276439836!3d41.0625229160824!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cacfed64fb350b%3A0x4b37de1b6547b5a0!2sNolena%20Global%20Pet%20%C3%9Cr%C3%BCnleri!5e0!3m2!1sen!2str!4v1784051522448!5m2!1sen!2str"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className="mt-8 h-[280px] w-full rounded-2xl border-0"
            />
          </Reveal>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-3xl border border-detail bg-surface p-6 sm:p-9"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-ink font-medium">
                    {t("contactForm.name")}
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name-input"
                    placeholder={t("contactForm.namePlaceholder")}
                    className="mt-2 bg-cream border-detail rounded-xl text-ink placeholder:text-ink/40 focus-visible:ring-brand focus-visible:border-brand h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-ink font-medium">
                    {t("contactForm.phone")}
                  </Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-phone-input"
                    placeholder="+90 5xx xxx xx xx"
                    className="mt-2 bg-cream border-detail rounded-xl text-ink placeholder:text-ink/40 focus-visible:ring-brand focus-visible:border-brand h-12"
                  />
                </div>
              </div>
              <div className="mt-5">
                <Label htmlFor="email" className="text-ink font-medium">
                  {t("contactForm.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email-input"
                  placeholder={t("contactForm.emailPlaceholder")}
                  className="mt-2 bg-cream border-detail rounded-xl text-ink placeholder:text-ink/40 focus-visible:ring-brand focus-visible:border-brand h-12"
                />
              </div>
              <div className="mt-5">
                <Label htmlFor="message" className="text-ink font-medium">
                  {t("contactForm.message")}
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message-input"
                  placeholder={t("contactForm.messagePlaceholder")}
                  rows={5}
                  className="mt-2 bg-cream border-detail rounded-xl text-ink placeholder:text-ink/40 focus-visible:ring-brand focus-visible:border-brand resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="mt-7 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand px-8 py-3.5 text-sm font-semibold text-cream hover:bg-brand2 transition-colors duration-200 disabled:opacity-70"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? t("contactForm.sending") : t("contactForm.send")}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
